import * as React from 'react';
import {forwardRef, Ref, useEffect, useImperativeHandle, useRef, useState} from 'react';
import Modal from "react-responsive-modal";
import AgHelpToolTip, {helpHeaderTemplate} from "../common/AgHeaderHelpToolTip";
import * as agGrid from "ag-grid-community";
import Axios from "../../../config/Axios";
import Utils from "../../../Utils";
import {AttributeTypeEnum} from "../enums/AttributeTypeEnum";
import urlencode from "urlencode";
import AgGridUtils from "../../../AgGridUtils";
import {useSelector} from "react-redux";
import {ResizeActions} from "../InstanceGridEditor";
import {GRID_TYPE} from "../instanceLayoutEditor/InstanceLayoutEditor";

const camelCase = require('camelcase');

const LookupInstanceModal = forwardRef((props: any, ref) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [isDataReady, setDataReady] = useState<boolean>(false);
    const [grid, _setGrid] = useState(null);
    const [data, setData] = useState(null);
    const [lookupBOC, setLookupBOC] = useState(null);
    const defaultInputRef: Ref<HTMLInputElement> = React.createRef();
    const locale = useSelector((store: any) => store.auth.locale);
    const [resizeAction, setAction] = useState(ResizeActions.FIT_CONTENT);
    const refGridType = useRef(null);

    useImperativeHandle(ref, () => {
        return {
            show: show,
            hide: hide
        };
    });

    useEffect(() => {
        if (isDataReady && isOpen) {
            let rowNode;
            if (data.value.id) {
                if (data.value.id !== -1) {
                    rowNode = grid.gridOptions.api.getRowNode(data.value.id);
                }
            } else {
                if (JSON.parse(data.value).id !== -1) {
                    const id = JSON.parse(data.value).id;
                    rowNode = grid.gridOptions.api.getRowNode(id);
                }
            }
            if (rowNode) {
                rowNode.setSelected(true);
            } else {
                if (grid.gridOptions.api.getSelectedRows().length === 0) {
                    Utils.$("#defaultInput").prop("checked", true);
                    Utils.$("#defaultInput").prop("disabled", true);
                }
            }
            setDataReady(false);
        }
    }, [isDataReady]);

    const show = (data: any, businessObjects: any[], gridType: GRID_TYPE) => {
        refGridType.current = gridType;
        setDataReady(false);
        for (let boc of businessObjects) {
            let found: any;
            for (let attribute of boc.attributes) {
                if (data.lookupAttributeId == attribute.id) {
                    found = true;
                    break;
                }
            }
            if (found) {
                setTimeout(() => {
                    buildGrid(boc);
                }, 1);
                setIsOpen(true);
                setData(data);
                setLookupBOC(boc);
                break;
            }
        }
    };

    const hide = () => {
        Axios.cancelTokenModals.cancel();
        setAction(ResizeActions.FIT_CONTENT);
        if (grid) {
            grid.destroy();
        }
        setIsOpen(false);
        setDataReady(false);
        setData(null);
        Axios.resetCancelTokenForModals();
        props.nextCellForEdit(refGridType.current);
    };

    const buildGrid = (businessObject) => {
        const columnDef: any [] = businessObject.attributes.map(item => {
            return {
                field: item.name.toLowerCase(),
                headerName: item.name,
                width: 50
            }
        });

        columnDef.unshift({
            field: "id",
            headerName: "ID",
            checkboxSelection: true,
            width: 50
        });

        const gridOptions: any = {
            defaultColDef: {
                resizable: true,
                sortable: true,
                suppressDragLeaveHidesColumns: true,
                filter: "agTextColumnFilter",
                tooltipComponent: AgHelpToolTip,
                filterParams: {
                    filterOptions: ['contains'],
                    applyButton: true,
                    clearButton: true,
                    suppressAndOrCondition: true
                },
                headerComponentParams: {
                    template: helpHeaderTemplate
                },
                floatingFilterComponentParams: {suppressFilterButton: true},
                menuTabs: ['generalMenuTab']
            },
            onGridReady: (params: any) => {
                params.api.sizeColumnsToFit();
            },
            onRowSelected: (params) => {
                if (params.api.getSelectedRows().length === 0) {
                    Utils.$("#defaultInput").prop("checked", true);
                    Utils.$("#defaultInput").prop("disabled", true);
                } else {
                    Utils.$("#defaultInput").prop("checked", false);
                    Utils.$("#defaultInput").prop("disabled", false);
                }
            },
            getRowNodeId: function (data) {
                return data.id;
            },
            onRowDoubleClicked: saveSelectedRelationship,
            suppressPropertyNamesCheck: true,
            columnDefs: columnDef,
            floatingFilter: true,
            rowModelType: 'serverSide',
            cacheBlockSize: 100,
            maxBlocksInCache: 500,
            maxConcurrentDatasourceRequests: 2,
            enableCellTextSelection: true,
            ensureDomOrder: true,
            rowSelection: 'single',
            animateRows: true,
            debug: false
        };

        const grid = new agGrid.Grid(document.getElementById("lookupGrid"), gridOptions);
        gridOptions.api.setServerSideDatasource(ServerSideDatasource(businessObject.id, "full"));
        _setGrid(grid);

        function saveSelectedRelationship(){
            Utils.$('#save-lookup-btn').click();
        }
    };

    const onSave = () => {
        const rows = grid.gridOptions.api.getSelectedRows();
        if (rows.length > 0) {
            props.save({
                id: data.id,
                instanceId: rows[0].id,
                name: rows[0][camelCase(lookupBOC.attributes.find(item => item.id == data.lookupAttributeId).name)]
            }, refGridType.current);
            hide();
        } else if (defaultInputRef.current.checked) {
            props.save({
                id: data.id,
                instanceId: -1,
                name: "n/a"
            }, refGridType.current);
            hide();
        }
    };

    const ServerSideDatasource = (id: number, dataView: string) => {
        return {
            getRows: (params: any) => {
                const limit = params.request.endRow - params.request.startRow;
                const page = (params.request.endRow / limit) - 1;
                const sort = AgGridUtils.generateSortData(params.request.sortModel);
                const filter = AgGridUtils.generateFilterData(params);
                Axios.instanceForModals.post(`boi/data/${id}?dataFormat=MAP&dataView=${urlencode(dataView)}&limit=${limit}&page=${page}`, {
                    filter: JSON.stringify(filter),
                    searchText: params.request.filterModel.quickFilter ? params.request.filterModel.quickFilter.filter : "",
                    sort: JSON.stringify(sort)
                })
                    .then((response: any) => {
                        const dataArray = response.data.content.map(item => {
                            const base = {
                                id: item.id,
                                tags: item.tags,
                                updated: Utils.moment(item.updated, Utils.moment.ISO_8601).isValid() ? Utils.moment.utc(item.updated).utcOffset(Utils.moment(item.updated).format("ZZ").toString()).format('YYYY-MM-DD HH:mm:ss') : item.updated
                            };
                            const custom = {};
                            Object.keys(item.data).map((key, index) => {
                                switch (response.data.dataView.attributes[index].attribute.type) {
                                    case AttributeTypeEnum.INT:
                                    case AttributeTypeEnum.DECIMAL:
                                        custom[key] = Utils.numberFormatByLocale(item.data[key], locale.value);
                                        break;
                                    case AttributeTypeEnum.TIMESTAMP:
                                        custom[key] = Utils.moment(item.data[key], Utils.moment.ISO_8601).isValid() ? Utils.moment.utc(item.data[key]).utcOffset(Utils.moment(item.data[key]).format("ZZ").toString()).format('YYYY-MM-DD HH:mm:ss') : item.data[key];
                                        break;
                                    case AttributeTypeEnum.LOOKUP:
                                        custom[key] = JSON.parse(item.data[key]).value;
                                        break;
                                    default:
                                        custom[key] = item.data[key];
                                }
                            });
                            return {
                                ...base,
                                ...custom
                            }
                        });
                        try {
                            const lastRow = dataArray.length < limit ? params.request.startRow + dataArray.length : -1;
                            params.successCallback(dataArray, lastRow);
                        } catch (e) {
                            console.error(e);
                        }

                        setDataReady(true);
                    })
                    .catch((e) => {
                        AgGridUtils.serverSideErrorHandler(e)
                        params.failCallback();
                    })
            }
        }
    };

    const autoSizeAllColumns = (params) => {
        let allColumnIds = [];
        params.columnApi.getAllColumns().forEach((column) => {
            allColumnIds.push(column.colId);
        });
        params.columnApi.autoSizeColumns(allColumnIds, false);

        if (resizeAction !== ResizeActions.FIT_SCREEN) {
            setAction(ResizeActions.FIT_SCREEN)
        }
    };

    const onResizeColumns = () => {
        if (resizeAction === ResizeActions.FIT_SCREEN) {
            fitScreenColumns();
        } else {
            autoSizeAllColumns(grid.gridOptions);
        }
    };

    const fitScreenColumns = () => {
        grid.gridOptions.columnApi.resetColumnState();
        grid.gridOptions.api.sizeColumnsToFit();
        if (resizeAction !== ResizeActions.FIT_CONTENT) {
            setAction(ResizeActions.FIT_CONTENT);
        }
    };

    return (
        <Modal
            center
            open={isOpen}
            focusTrapped={false}
            styles={{
                overlay: {zIndex: 10000},
                closeButton: {display: "none"},
                modal: {
                    width: 900,
                    padding: 0
                }
            }}
            closeOnEsc={true}
            closeOnOverlayClick={true}
            onClose={() => {
                hide();
            }}
        >
            <div style={{width: "100%", height: "calc(100vh - 100px)"}}>
                <header className={'instance-layout-grid-header'} style={{height: 40}}>
                    <h5>Lookup Instance Modal</h5>
                    <button onClick={onResizeColumns}
                            style={{height: 33, marginTop: 3, marginRight: 3}}
                            className="btn new-style-btn">
                        {resizeAction === ResizeActions.FIT_CONTENT ?
                            <i className="fa fa-expand"> Fit Content</i> :
                            <i className="fas fa-compress-arrows-alt"> Fit screen</i>}
                    </button>
                </header>
                <div id={"lookupGrid"} className={"ag-theme-balham-dark ag-grids"}
                     style={{width: "100%", height: "calc(100% - 40px)"}}/>
                <div style={{background: "#2D3436", height: 40, width: "100%"}}>
                    <div style={{float: 'left', display: "flex", width: 94, height: 25, marginTop: 6, marginLeft: 5}}>
                        <label style={{color: "white", fontSize: 20, marginRight: 5}}>Empty</label>
                        <input onChange={() => {
                            if (defaultInputRef.current.checked) {
                                grid.gridOptions.api.deselectAll();
                            }
                        }} id={"defaultInput"} ref={defaultInputRef} style={{width: 20, height: 20}} name={"default"}
                               type={"checkbox"}/>
                    </div>
                    <button
                        onClick={onSave}
                        className={"btn"}
                        id='save-lookup-btn'
                        style={{float: "right", marginTop: 3, marginRight: 2}}>
                        <i className="fas fa-save"> Save</i>
                    </button>
                    <button
                        onClick={() => {
                            hide();
                        }}
                        className={"btn"}
                        id={'close-modal-btn'}
                        style={{float: "right", marginTop: 3, marginRight: 2}}>
                        <i className="fas fa-times"> Close</i>
                    </button>
                    <h5 className={'lookup-modal-footer-text'}>Press ESC to exit</h5>
                </div>
            </div>
        </Modal>
    )
});

export default LookupInstanceModal;



// WEBPACK FOOTER //
// ./app/components/bo-core/modals/LookupInstanceModal.tsx